<script setup lang="ts">
import { computed } from 'vue';
import type { ComputedRef } from 'vue';
import Card from 'primevue/card';
import Skeleton from 'primevue/skeleton';
import BaseIcon from '@/components/base/BaseIcon.vue';
import type { KycCheck } from '@/types/kyc';
import {
  getDaysSinceUpdate,
  getCategoryIcon,
  getRiskCategoryLabel,
  getRiskLevelDisplay
} from '@/utils/kyc';

interface Props {
  checks: KycCheck[];
  loading?: boolean;
}

// --- Component Setup ---

const props = withDefaults(defineProps<Props>(), {
  checks: () => [],
  loading: false
});

const skeletonCount: ComputedRef<number> = computed(() => (props.checks.length || 12));

const gridLayout: ComputedRef<string> = computed(() => {
  const cardCount = (props.loading ? skeletonCount.value : props.checks.length);

  const layouts: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
    5: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5',
    6: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6',
    8: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-6',
    9: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6',
    10: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
    12: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6',
    13: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6',
    15: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
    16: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8',
    18: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6',
    20: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5',
    24: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6'
  };

  return layouts[cardCount] || 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6';
});

const getCheckRiskDisplay = (check: KycCheck) => (
  check.status === 'missing'
    ? getRiskLevelDisplay(check.highestRiskLevelValue, { isMissing: true })
    : getRiskLevelDisplay(check.highestRiskLevelValue)
);

const hasCheckRiskValue = (check: KycCheck): boolean =>
  check.highestRiskLevelValue !== null && check.highestRiskLevelValue !== undefined;

const getCheckTitle = (check: KycCheck): string =>
  getRiskCategoryLabel(check.category_slug, check.title, true);

</script>

<template>
  <!-- KYC Checks Grid -->
  <div v-if="loading" :class="`grid ${gridLayout} gap-3`" style="grid-auto-rows: 1fr;">
    <Card v-for="n in skeletonCount" :key="n" class="flex flex-col h-full">
      <template #content>
        <div class="flex flex-col h-full">
          <!-- Header with Icon and Title -->
          <div class="flex items-start gap-3 mb-3">
            <div class="flex-shrink-0">
              <Skeleton width="1.75rem" height="1.75rem" class="flex-shrink-0"/>
            </div>
            <div class="flex-1 min-w-0">
              <Skeleton width="75%" height="0.875rem" class="mb-1"/>
              <Skeleton width="60%" height="0.75rem"/>
            </div>
          </div>

          <!-- Main Status Section -->
          <div class="flex items-center justify-between mb-1.5 flex-grow">
            <div class="flex-1">
              <Skeleton width="50%" height="0.875rem" class="mb-1"/>
              <Skeleton width="80%" height="0.75rem"/>
            </div>
            <div class="flex-shrink-0">
              <Skeleton width="2.5rem" height="2.5rem" shape="circle"/>
            </div>
          </div>

          <!-- Outdated Warning Placeholder -->
          <div class="mb-1.5">
            <Skeleton width="90%" height="0.75rem"/>
          </div>

          <!-- Action Button -->
          <div class="pt-1 border-t border-gray-100 mt-auto">
            <Skeleton width="35%" height="0.75rem"/>
          </div>
        </div>
      </template>
    </Card>
  </div>

  <div v-else-if="checks.length > 0" :class="`grid ${gridLayout} gap-3`"
       style="grid-auto-rows: 1fr;">
    <Card v-for="check in checks" :key="check.id" 
          :class="[
            'transition-all duration-200 hover:shadow-lg border-l-4 flex flex-col h-full',
            getCheckRiskDisplay(check).bgClass,
            getCheckRiskDisplay(check).borderClass
          ]">
      <template #content>
        <div class="flex flex-col h-full">
          <!-- Header: Icon + Title -->
          <div class="flex items-start gap-2 mb-3">
            <div class="flex-shrink-0">
              <BaseIcon class="text-[28px] text-gray-800">{{ getCategoryIcon(check.category_slug) }}</BaseIcon>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="font-semibold text-gray-800 text-sm leading-tight">{{
                  getCheckTitle(check)
                }}</h3>
            </div>
          </div>

          <!-- Main Content: Risk Level + Entity Count -->
          <div class="flex items-center justify-between mb-1.5 flex-grow">
            <div class="flex-1">
              <!-- Risk Level - Primary -->
              <p :class="['text-lg font-bold flex items-center gap-1.5', getCheckRiskDisplay(check).colorClass]">
                <span>{{ getCheckRiskDisplay(check).label }}<span v-if="hasCheckRiskValue(check)"> Risco</span></span>
                <BaseIcon class="text-2xl">{{ getCheckRiskDisplay(check).icon }}</BaseIcon>
              </p>
              <!-- Entity Risk Count -->
              <p class="text-gray-600 text-sm mt-1">
                <span v-if="check.entitiesFailed > 0" :class="getCheckRiskDisplay(check).colorClass">
                  <strong>{{ check.entitiesFailed }}</strong> de {{ check.entitiesChecked }}
                </span>
                <span v-else class="text-green-600">
                  <strong>0</strong> de {{ check.entitiesChecked }}
                </span>
                {{ check.entitiesChecked === 1 ? 'entidade sinalizada' : 'entidades sinalizadas' }}
              </p>
            </div>
          </div>

          <!-- Outdated Warning -->
          <div v-if="check.isOutdated"
               class="flex items-center gap-1.5 text-xs mb-1.5 text-gray-400">
            <BaseIcon class="text-sm">emergency</BaseIcon>
            <span>Desatualizado ({{ getDaysSinceUpdate(check.lastUpdated) }} dias)</span>
          </div>


          <!-- Action Button -->
           <!-- TODO: Add action button -->
            <!--
          <div class="pt-1 border-t border-gray-100 mt-auto">
            <Button label="Ver detalhes" link size="small"
                    class="text-blue-600 hover:text-blue-800 text-xs p-0 h-auto"/>
          </div> -->
        </div>
      </template>
    </Card>
  </div>

  <!-- Empty State -->
  <div v-else class="text-center py-12">
    <BaseIcon class="text-6xl text-gray-300 mb-4">inbox</BaseIcon>
    <h3 class="text-xl font-semibold text-gray-900 mb-2">Nenhuma verificação KYX disponível</h3>
    <p class="text-gray-600">Nenhum dado de verificação KYX foi encontrado para esta empresa.</p>
  </div>
</template>
